import React, { useContext, useState } from "react";
import { Grid, Typography } from "@mui/material";

import { MainContext } from "../../../state/state";
import VideoDropZone from "../../../components/VideoDropZone";
import ProgressModal, { UploadProgress } from "src/modal/ProgressModal";

import sendRequest from "../../../helpers/service";
import { toast } from "react-toastify";
import handleError from "src/helpers/errorHandler";
import openDeleteModal from "src/helpers/deleteHandler";
import { toastMessage } from "src/helpers/mixins";
import axios from "axios";

interface ContentTabProps {
  permission: any;
  detail: any;
  setDetail: React.Dispatch<React.SetStateAction<any>>;
}

export default function ContentTab({ permission, detail, setDetail }: ContentTabProps) {
  const { setLoading } = useContext(MainContext);

  const [progressLoading, setProgressLoading] = useState<UploadProgress>();

  const [trailer, setTrailer] = useState(detail.trailer);

  const handleSubmit = () => {
    submitAction();
  };

  const submitAction = async () => {
    try {
      if (!trailer || typeof trailer === "string") {
        toastMessage("You need to select a trailer video", "error");
        return;
      }

      setProgressLoading({ preparing: true, progress: 0 });
      const { data: urlData } = await sendRequest.post<any>({
        url: "/live/get-upload-url",
        data: {
          content_id: detail.id,
          file_type: trailer.type,
          content_type: "trailer",
        },
      });

      // Upload the file
      await axios.put(urlData.upload_url, trailer, {
        headers: { "Content-Type": urlData.content_type },
        onUploadProgress: (e) => {
          setProgressLoading({
            preparing: false,
            progress: Math.round((e.loaded * 100) / e.total),
            loaded: e.loaded,
            total: e.total,
          });
        },
      });

      // Get duration
      const video = document.createElement("video");
      video.preload = "metadata";
      const duration = await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          resolve(Math.round(video.duration));
          URL.revokeObjectURL(video.src);
        };
        video.src = URL.createObjectURL(trailer as File);
      });

      await sendRequest.post({
        url: "/live/complete-upload",
        data: {
          content_id: detail.id,
          path: urlData.path,
          duration,
          content_type: "trailer",
        },
      });

      toastMessage("Trailer uploaded successfully", "success");
      setProgressLoading(undefined);
      setTrailer(urlData.path);
      setDetail((prev) => ({ ...prev, trailer: urlData.path }));
    } catch (error) {
      console.log("Upload Trailer Error: ", error);
      handleError(error);
      setProgressLoading(undefined);
    }
  };

  const handleDelete = async () => {
    openDeleteModal({
      modals: [
        {
          title: `Delete Trailer`,
          message: `Are you sure to delete this trailer? <br/> <b>This cannot be reverted</b>`,
          confirmButton: {
            text: "Delete",
            color: "red",
          },
          cancelButton: {
            text: "Cancel",
            color: "black",
          },
        },
      ],
      apiUrl: `/live/delete-content/trailer/${detail.id}`,
    }).then(() => {
      setDetail((detail) => {
        detail.trailer = null;
        setTrailer(null);
        return detail;
      });
      toast("Trailer deleted successfully", {
        type: "success",
        theme: "colored",
      });
    });
  };

  return (
    <Grid container>
      {progressLoading && (
        <ProgressModal
          data={progressLoading}
          onClose={() => setProgressLoading(undefined)}
        />
      )}
      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          mb={3}
        >
          {!progressLoading && (
            <VideoDropZone
              playerId="trailer-player"
              value={trailer}
              label={"Trailer"}
              onChange={setTrailer}
              accept={{
                "video/mp4": [".mp4"],
              }}
              disabled={!permission?.live?.write}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          pl={4}
        >
          <Typography fontWeight={700}>Trailer Information</Typography>
          <Typography>• Upload a trailer video for the live event</Typography>
          <Typography>• Supported format: MP4</Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
